import { GetIRLDoc, PostFolderDocuments, startUploadBatch, updateUploadTrayItem } from '../redux/documentsSlice';
import { GetDataroomItemInfo } from '../redux/dataroomSlice';
import { isRagIndexableFileName, makeUploadClientId } from './vdrUploadUtils';

/** Matches classic VDR / API `VDR_BULK_UPLOAD_MAX_BATCH` (default 50). */
export const VDR_UPLOAD_BATCH_SIZE = 50;

/** Soft cap per user action (multi-file or folder tree). */
export const VDR_UPLOAD_MAX_FILES = 1000;

const GENERIC_UPLOAD_ERROR = 'Upload failed. Please try again.';

const looksLikeHtml = value => {
    if (typeof value !== 'string') return false;
    const text = value.trim();
    return (
        /<!DOCTYPE\s+html/i.test(text) ||
        /<html[\s>]/i.test(text) ||
        /<\/?[a-z][\s\S]*>/i.test(text.slice(0, 200))
    );
};

const isUsableApiMessage = value => {
    if (typeof value !== 'string') return false;
    const text = value.replace(/\s+/g, ' ').trim();
    if (!text || text.length > 180) return false;
    if (looksLikeHtml(text)) return false;
    return true;
};

export const extractUploadErrorMessage = payload => {
    if (!payload) return GENERIC_UPLOAD_ERROR;
    if (typeof payload === 'string') {
        return isUsableApiMessage(payload) ? payload.trim() : GENERIC_UPLOAD_ERROR;
    }
    if (typeof payload !== 'object') return GENERIC_UPLOAD_ERROR;

    const candidates = [payload.detail, payload.message, payload.error];
    for (const candidate of candidates) {
        if (isUsableApiMessage(candidate)) return String(candidate).trim();
        if (Array.isArray(candidate) && isUsableApiMessage(candidate[0])) {
            return String(candidate[0]).trim();
        }
    }

    const firstKey = Object.keys(payload)[0];
    const firstVal = firstKey ? payload[firstKey] : null;
    if (Array.isArray(firstVal) && isUsableApiMessage(firstVal[0])) {
        return String(firstVal[0]).trim();
    }
    if (isUsableApiMessage(firstVal)) return String(firstVal).trim();

    return GENERIC_UPLOAD_ERROR;
};

export function filterVdrUploadFiles(files) {
    return Array.from(files || []).filter(
        file => file?.name && !String(file.name).startsWith('.') && file.size > 0,
    );
}

export function validateVdrUploadFileCount(count) {
    const n = Number(count) || 0;
    if (n <= 0) {
        return { ok: false, message: 'No files to upload.' };
    }
    if (n > VDR_UPLOAD_MAX_FILES) {
        return {
            ok: false,
            message: `You can upload up to ${VDR_UPLOAD_MAX_FILES} files at once. Split into smaller batches.`,
        };
    }
    return { ok: true, message: '' };
}

export function listVdrUploadBatches(entries, batchSize = VDR_UPLOAD_BATCH_SIZE) {
    const size = Math.max(1, batchSize);
    const batches = [];
    for (let offset = 0; offset < entries.length; offset += size) {
        const slice = entries.slice(offset, offset + size);
        batches.push({
            entries: slice,
            triggerIndexer: offset + size >= entries.length,
        });
    }
    return batches;
}

const folderPathForFile = file => {
    const relative = file.webkitRelativePath || file.name || '';
    const slash = relative.lastIndexOf('/');
    return slash >= 0 ? relative.slice(0, slash) : '';
};

export function buildVdrUploadEntries(files, { flatIntoFolder = false } = {}) {
    return filterVdrUploadFiles(files).map(file => ({
        file,
        folderPath: flatIntoFolder ? '' : folderPathForFile(file),
        clientId: makeUploadClientId(),
    }));
}

function applyDocToTrayItem(dispatch, entry, doc, workstreamId) {
    const storedName = doc?.document_name || entry.file.name;
    const documentId = doc?.id != null ? Number(doc.id) : undefined;
    if (documentId && isRagIndexableFileName(storedName)) {
        dispatch(
            updateUploadTrayItem({
                clientId: entry.clientId,
                fileName: storedName,
                status: 'indexing',
                documentId,
                wsId: Number(workstreamId),
                startedAt: Date.now(),
            }),
        );
        return true;
    }
    dispatch(
        updateUploadTrayItem({
            clientId: entry.clientId,
            fileName: storedName,
            status: documentId ? 'ready' : 'failed',
            error: documentId ? undefined : GENERIC_UPLOAD_ERROR,
            documentId,
            wsId: Number(workstreamId),
        }),
    );
    return Boolean(documentId);
}

/**
 * Upload many files via bulk_folder_upload in batches (50 default).
 * Indexer + rollup semantics: trigger_indexer only on the last batch; API queues
 * chunk labelling on every batch.
 */
export async function runVdrBulkUploadWithTray({
    dispatch,
    files,
    folder,
    workstreamId,
    projectId,
    vdrId,
    uploadBy,
    destinationLabel,
    flatIntoFolder = false,
    refreshWorkstreamList = true,
}) {
    const uploadEntries = buildVdrUploadEntries(files, { flatIntoFolder });
    const validation = validateVdrUploadFileCount(uploadEntries.length);
    if (!validation.ok) {
        return { started: false, successCount: 0, failCount: 0, message: validation.message };
    }

    if (!workstreamId) {
        return { started: false, successCount: 0, failCount: 0, message: 'Workstream is required.' };
    }

    if (flatIntoFolder && !folder?.id) {
        return { started: false, successCount: 0, failCount: 0, message: 'Select a destination folder.' };
    }

    const trayLabel =
        destinationLabel ||
        (flatIntoFolder
            ? folder?.name || 'VDR'
            : uploadEntries[0]?.file.webkitRelativePath?.split('/')?.[0] || folder?.name || 'Folder upload');

    dispatch(
        startUploadBatch({
            destinationLabel: trayLabel,
            items: uploadEntries.map(entry => ({
                clientId: entry.clientId,
                fileName: entry.file.name,
                folderName: trayLabel,
                status: 'queued',
            })),
        }),
    );

    let successCount = 0;
    let failCount = 0;
    const batches = listVdrUploadBatches(uploadEntries);

    for (const batch of batches) {
        const { entries: batchEntries, triggerIndexer } = batch;
        batchEntries.forEach(entry => {
            dispatch(updateUploadTrayItem({ clientId: entry.clientId, status: 'uploading' }));
        });

        const formData = new FormData();
        batchEntries.forEach(entry => {
            formData.append('document', entry.file);
            formData.append('path', entry.folderPath);
            formData.append('document_name', entry.file.name);
        });
        formData.append('project', projectId);
        if (vdrId) formData.append('vdr', vdrId);
        if (uploadBy != null) formData.append('upload_by', uploadBy);
        if (folder?.id) formData.append('ws_folder', folder.id);
        formData.append('trigger_indexer', triggerIndexer ? 'true' : 'false');

        try {
            const res = await dispatch(PostFolderDocuments({ body: formData, ws_id: workstreamId }));
            if (res?.type === 'POST_FOLDER_DOC/rejected') {
                const error = extractUploadErrorMessage(res?.payload);
                failCount += batchEntries.length;
                batchEntries.forEach(entry => {
                    dispatch(updateUploadTrayItem({ clientId: entry.clientId, status: 'failed', error }));
                });
                continue;
            }

            const docs = Array.isArray(res?.payload) ? res.payload : [];
            batchEntries.forEach((entry, index) => {
                const doc = docs[index];
                if (applyDocToTrayItem(dispatch, entry, doc, workstreamId)) {
                    successCount += 1;
                } else if (doc?.id) {
                    successCount += 1;
                } else {
                    failCount += 1;
                }
            });
        } catch (err) {
            const error = extractUploadErrorMessage(err?.response?.data || err?.message || err);
            failCount += batchEntries.length;
            batchEntries.forEach(entry => {
                dispatch(updateUploadTrayItem({ clientId: entry.clientId, status: 'failed', error }));
            });
        }
    }

    if (refreshWorkstreamList && workstreamId) {
        await dispatch(GetDataroomItemInfo(workstreamId));
        await dispatch(GetIRLDoc(workstreamId));
    }

    return { started: true, successCount, failCount, message: '' };
}
